import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import ChangePassword from '@/pages/ChangePassword';
import AdminGroups from '@/pages/admin/Groups';
import FamilyPortal from '@/pages/FamilyPortal';
import ClassesPage from '@/pages/Classes';
import Challenges from '@/pages/student/Challenges';
import LoginHistory from '@/pages/LoginHistory';
import CoachHistory from '@/pages/student/CoachHistory';
import ClassroomsList from '@/pages/ClassroomsList';
import ClassroomLive from '@/pages/ClassroomLive';
import RecapsList from '@/pages/RecapsList';
import RecapView from '@/pages/RecapView';
import AuditLog from '@/pages/admin/AuditLog';
import AppLayout from '@/layouts/AppLayout';
import StudentDashboard from '@/pages/student/Dashboard';
import CoursesPage from '@/pages/student/Courses';
import CourseDetail from '@/pages/student/CourseDetail';
import LessonView from '@/pages/student/LessonView';
import ExercisesPage from '@/pages/student/Exercises';
import ExerciseSolver from '@/pages/student/ExerciseSolver';
import ChessLab from '@/pages/student/ChessLab';
import GamesPage from '@/pages/student/Games';
import GameViewer from '@/pages/student/GameViewer';
import AchievementsPage from '@/pages/student/Achievements';
import StudentProfile from '@/pages/student/Profile';
import LichessPage from '@/pages/student/Lichess';
import Assignments from '@/pages/student/Assignments';
import StudentQuizzes from '@/pages/student/Quizzes';
import { QuizTeacherList, QuizTeacherDetail } from '@/pages/teacher/Quizzes';
import QuizStart from '@/pages/student/QuizStart';
import TeacherDashboard from '@/pages/teacher/Dashboard';
import TeacherGroups from '@/pages/teacher/Groups';
import TeacherAssignments from '@/pages/teacher/Assignments';
import TeacherExercises from '@/pages/teacher/Exercises';
import PGNImport from '@/pages/teacher/PGNImport';
import AdminDashboard from '@/pages/admin/Dashboard';
import AdminUsers from '@/pages/admin/Users';
import ModuleExamRequests from '@/pages/admin/ModuleExamRequests';
import ModuleExamReviews from '@/pages/admin/ModuleExamReviews';
import AdminTasks from '@/pages/admin/AdminTasks';
import AdminAchievements from '@/pages/admin/Achievements';
import AdminCourses from '@/pages/admin/Courses';
import ProfilePage from '@/pages/Profile';
import DatabasePage from '@/pages/Database';
import StudiesPage from '@/pages/Studies';
import StudyDetail from '@/pages/StudyDetail';

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-slate-500">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'super_admin' || user.role === 'school_admin' || user.role === 'partner_admin' || user.role === 'assistant_admin') return <Navigate to="/admin/dashboard" replace />;
  if (user.role === 'teacher') return <Navigate to="/teacher/dashboard" replace />;
  return <Navigate to="/student/dashboard" replace />;
}

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/family/:token" element={<FamilyPortal />} />
            <Route path="/recap/:token" element={<RecapView />} />
            {/* UX-1: Classroom es una experiencia independiente — sin el
                AppLayout global (sidebar 256px + header móvil). Mantiene su
                propio shell full-screen y guards de autenticación. */}
            <Route path="/classroom/:id" element={<Protected><ClassroomLive /></Protected>} />
            <Route path="/app" element={<RoleRedirect />} />

            <Route element={<Protected roles={['student']}><AppLayout /></Protected>}>
              <Route path="/student/dashboard" element={<StudentDashboard />} />
              <Route path="/student/courses" element={<CoursesPage />} />
              <Route path="/student/courses/:id" element={<CourseDetail />} />
              <Route path="/student/lessons/:id" element={<LessonView />} />
              <Route path="/student/exercises" element={<ExercisesPage />} />
              <Route path="/student/exercises/:id" element={<ExerciseSolver />} />
              <Route path="/student/quizzes" element={<StudentQuizzes />} />
              <Route path="/student/quizzes/:aid" element={<QuizStart />} />
              <Route path="/student/assignments" element={<Assignments />} />
              <Route path="/student/chess" element={<ChessLab />} />
              <Route path="/student/games" element={<GamesPage />} />
              <Route path="/student/games/:id" element={<GameViewer />} />
              <Route path="/student/achievements" element={<AchievementsPage />} />
              <Route path="/student/profile" element={<StudentProfile />} />
              <Route path="/student/lichess" element={<LichessPage />} />
              <Route path="/student/classes" element={<ClassesPage />} />
              <Route path="/student/challenges" element={<Challenges />} />
              <Route path="/student/login-history" element={<LoginHistory />} />
              <Route path="/student/coach-history" element={<CoachHistory />} />
              <Route path="/student/classrooms" element={<ClassroomsList />} />
            </Route>

            <Route element={<Protected roles={['teacher']}><AppLayout /></Protected>}>
              <Route path="/teacher/dashboard" element={<TeacherDashboard />} />
              <Route path="/teacher/groups" element={<TeacherGroups />} />
              <Route path="/teacher/assignments" element={<TeacherAssignments />} />
              <Route path="/teacher/exercises" element={<TeacherExercises />} />
              <Route path="/teacher/pgn" element={<PGNImport />} />
              <Route path="/teacher/games" element={<GamesPage />} />
              <Route path="/teacher/classes" element={<ClassesPage />} />
              <Route path="/teacher/login-history" element={<LoginHistory />} />
              <Route path="/teacher/classrooms" element={<ClassroomsList />} />
              <Route path="/teacher/recaps" element={<RecapsList />} />
              <Route path="/teacher/profile" element={<ProfilePage />} />
              <Route path="/teacher/database" element={<DatabasePage />} />
              <Route path="/teacher/studies" element={<StudiesPage />} />
              <Route path="/teacher/studies/:id" element={<StudyDetail />} />
              <Route path="/teacher/quizzes" element={<QuizTeacherList />} />
              <Route path="/teacher/quizzes/:quizId" element={<QuizTeacherDetail />} />
            </Route>

            <Route element={<Protected roles={['super_admin', 'school_admin', 'partner_admin', 'assistant_admin']}><AppLayout /></Protected>}>
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/module-exam-requests" element={<ModuleExamRequests />} />
              <Route path="/admin/module-exam-reviews" element={<ModuleExamReviews />} />
              <Route path="/admin/tasks" element={<AdminTasks />} />
              <Route path="/admin/groups" element={<AdminGroups />} />
              <Route path="/admin/courses" element={<AdminCourses />} />
              <Route path="/admin/achievements" element={<AdminAchievements />} />
              <Route path="/admin/audit" element={<AuditLog />} />
              <Route path="/admin/login-history" element={<LoginHistory />} />
              <Route path="/admin/profile" element={<ProfilePage />} />
              <Route path="/admin/classrooms" element={<ClassroomsList />} />
              <Route path="/admin/database" element={<DatabasePage />} />
              <Route path="/admin/studies" element={<StudiesPage />} />
              <Route path="/admin/studies/:id" element={<StudyDetail />} />
              <Route path="/admin/quizzes" element={<QuizTeacherList />} />
              <Route path="/admin/quizzes/:quizId" element={<QuizTeacherDetail />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
